import type { Layout } from '../core/types';
import { $ } from './dom';
import { setDocked, type Tab } from './sheet';

/**
 * Layouts rearrange the main screen; CSS keys off `html[data-layout]`. Desk and Logbook also
 * "dock" a panel pane (tasks / stats) beside the timer by moving its nodes into #sidePanel, so
 * every existing renderer and listener keeps working on the very same elements.
 */
const DOCKS: Partial<Record<Layout, { tab: Tab; label: string }>> = {
  desk: { tab: 'tasks', label: 'Tasks' },
  logbook: { tab: 'stats', label: "Today's progress" },
};

const panel = $('#sidePanel');
let dockedTab: Tab | null = null;
let current: Layout = 'minimal';
const listeners: ((l: Layout) => void)[] = [];

export const currentLayout = () => current;
export const onLayout = (fn: (l: Layout) => void) => listeners.push(fn);

function undock() {
  if (!dockedTab) return;
  $(`#pane-${dockedTab}`).append(...panel.childNodes);
  panel.hidden = true;
  delete panel.dataset.pane;
  dockedTab = null;
}

function dock(tab: Tab, label: string) {
  panel.append(...$(`#pane-${tab}`).childNodes);
  panel.dataset.pane = tab;
  panel.setAttribute('aria-label', label);
  panel.tabIndex = -1; // focus target when its header button is used
  panel.hidden = false;
  dockedTab = tab;
}

export function applyLayout(l: Layout) {
  if (l === current && document.documentElement.dataset.layout === l && (DOCKS[l]?.tab ?? null) === dockedTab)
    return;
  current = l;
  document.documentElement.dataset.layout = l;
  const want = DOCKS[l];
  if ((want?.tab ?? null) !== dockedTab) {
    undock();
    if (want) dock(want.tab, want.label);
  }
  setDocked(want ? [want.tab] : []);
  listeners.forEach((fn) => fn(l));
}

/** The circular dial (and dragging it) only exists in some layouts. */
export const hasDial = () => current === 'minimal' || current === 'desk' || current === 'logbook';

/* Horizon: while running, the chrome recedes after a few quiet seconds; any activity wakes it. */
let calmTimer: ReturnType<typeof setTimeout> | undefined;
function wake() {
  document.body.classList.remove('calm');
  clearTimeout(calmTimer);
  if (current === 'horizon') calmTimer = setTimeout(() => document.body.classList.add('calm'), 3500);
}

export function initLayout() {
  for (const ev of ['pointermove', 'pointerdown', 'keydown', 'focusin', 'wheel'])
    window.addEventListener(ev, wake, { passive: true });
  onLayout(wake);
}
